import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchDashboardCockpit,
  fetchDashboardCockpitAnalyticsSlice,
  fetchDashboardCockpitTodayComparison,
} from '../services/dashboard';
import type {
  DashboardCockpitAnalyticsSliceName,
  DashboardCockpitPatternsRootSlice,
  DashboardCockpitProductMix,
  DashboardCockpitResponse,
  DashboardCockpitTodayComparisonResponse,
} from '../types';

const DEFAULT_HISTORY_DAYS = 30;
export const DASHBOARD_CARRIER_ANALYTICS_ENABLED = false;
const DASHBOARD_ANALYTICS_SLICE_SEQUENCE: DashboardCockpitAnalyticsSliceName[] = [
  'history',
  'carrier',
  'patterns-human-attendance',
  'campaign-base-health',
  'conversation-quality',
  'patterns-ai',
  'patterns-reverse-ivr',
];

export type DashboardCockpitAnalyticsSliceStatus = 'idle' | 'loading' | 'success' | 'error' | 'skipped';
export type DashboardCockpitTodayComparisonStatus = 'idle' | 'loading' | 'success' | 'error' | 'skipped';

export interface DashboardCockpitAnalyticsSliceState {
  status: DashboardCockpitAnalyticsSliceStatus;
  error: Error | null;
}

export interface DashboardCockpitTodayComparisonState {
  status: DashboardCockpitTodayComparisonStatus;
  error: Error | null;
}

export type DashboardCockpitAnalyticsState = Record<
  DashboardCockpitAnalyticsSliceName,
  DashboardCockpitAnalyticsSliceState
>;

function buildAnalyticsSliceQueue(productMix: DashboardCockpitProductMix | null): DashboardCockpitAnalyticsSliceName[] {
  /**
   * Preserve a deterministic order so large tenants do not start every
   * historical aggregate at once. Product-gated slices are skipped only when
   * the backend has already said the product does not exist for this tenant.
   */
  return DASHBOARD_ANALYTICS_SLICE_SEQUENCE.filter((sliceName) => {
    if (sliceName === 'history') {
      return true;
    }
    if (sliceName === 'carrier') {
      // Temporarily disabled while the carrier dashboard panel is redesigned.
      return DASHBOARD_CARRIER_ANALYTICS_ENABLED;
    }
    if (sliceName === 'patterns-reverse-ivr') {
      return Boolean(productMix?.hasReverseIvrProduct);
    }
    if (sliceName === 'patterns-human-attendance') {
      return Boolean(productMix?.hasTelephonyPack);
    }
    return Boolean(productMix?.hasAiProduct);
  });
}

function buildAnalyticsState(productMix: DashboardCockpitProductMix | null): DashboardCockpitAnalyticsState {
  const queue = new Set(buildAnalyticsSliceQueue(productMix));
  return DASHBOARD_ANALYTICS_SLICE_SEQUENCE.reduce((state, sliceName) => {
    state[sliceName] = {
      status: queue.has(sliceName) ? 'idle' : 'skipped',
      error: null,
    };
    return state;
  }, {} as DashboardCockpitAnalyticsState);
}

function mergeDashboardCockpitPatterns(
  current: DashboardCockpitPatternsRootSlice,
  incoming: DashboardCockpitPatternsRootSlice,
): DashboardCockpitPatternsRootSlice {
  /**
   * Slice endpoints return the full envelope with only one branch populated.
   * Nulls therefore mean "not part of this response" during a single load
   * cycle, because the base state is reset from the latest cockpit snapshot.
   */
  return {
    windowDays: incoming.windowDays || current.windowDays,
    consumptionEvolution: incoming.consumptionEvolution ?? current.consumptionEvolution,
    performanceEvolution: incoming.performanceEvolution ?? current.performanceEvolution,
    efficiencyEvolution: incoming.efficiencyEvolution ?? current.efficiencyEvolution,
    conversationQualityEvolution: incoming.conversationQualityEvolution ?? current.conversationQualityEvolution,
    campaignBaseHealth: incoming.campaignBaseHealth ?? current.campaignBaseHealth,
    carrier: incoming.carrier ?? current.carrier,
    ai: incoming.ai ?? current.ai,
    reverseIvr: incoming.reverseIvr ?? current.reverseIvr,
    humanAttendance: incoming.humanAttendance ?? current.humanAttendance,
  };
}

function shouldLoadTodayComparison(productMix: DashboardCockpitProductMix | null): boolean {
  return Boolean(
    productMix?.hasAiProduct
    || productMix?.hasReverseIvrProduct
    || productMix?.hasTelephonyPack,
  );
}

function mergeDashboardCockpitTodayComparison(
  current: DashboardCockpitResponse,
  incoming: DashboardCockpitTodayComparisonResponse,
): DashboardCockpitResponse {
  /**
   * The comparison endpoint returns only the heavier baseline fields. Keep the
   * rest of the cockpit snapshot intact so live/runtime cards do not flicker.
   */
  return {
    ...current,
    today: {
      ...current.today,
      ai: current.today.ai
        ? {
            ...current.today.ai,
            recentComparison: incoming.today.ai,
          }
        : current.today.ai,
      reverseIvr: current.today.reverseIvr
        ? {
            ...current.today.reverseIvr,
            recentComparison: incoming.today.reverseIvr,
          }
        : current.today.reverseIvr,
      humanAttendance: current.today.humanAttendance
        ? {
            ...current.today.humanAttendance,
            recentComparison: incoming.today.humanAttendance,
          }
        : current.today.humanAttendance,
    },
  };
}

/**
 * Load the dashboard cockpit snapshot first, then hydrate analytics slices one by one.
 */
export function useDashboardCockpit(historyDays: number = DEFAULT_HISTORY_DAYS) {
  const [cockpit, setCockpit] = useState<DashboardCockpitResponse | null>(null);
  const [patterns, setPatterns] = useState<DashboardCockpitPatternsRootSlice | null>(null);
  const [analyticsSlices, setAnalyticsSlices] = useState<DashboardCockpitAnalyticsState>(() => buildAnalyticsState(null));
  const [todayComparisonStatus, setTodayComparisonStatus] = useState<DashboardCockpitTodayComparisonState>({
    status: 'idle',
    error: null,
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const requestIdRef = useRef(0);

  const loadAnalyticsSlice = useCallback(async (
    sliceName: DashboardCockpitAnalyticsSliceName,
    requestId: number,
  ) => {
    setAnalyticsSlices((current) => ({
      ...current,
      [sliceName]: {
        status: 'loading',
        error: null,
      },
    }));

    try {
      const response = await fetchDashboardCockpitAnalyticsSlice(sliceName, historyDays);
      if (requestIdRef.current !== requestId) {
        return;
      }
      setPatterns((current) => (
        current ? mergeDashboardCockpitPatterns(current, response.patterns) : response.patterns
      ));
      setAnalyticsSlices((current) => ({
        ...current,
        [sliceName]: {
          status: 'success',
          error: null,
        },
      }));
    } catch (err) {
      if (requestIdRef.current !== requestId) {
        return;
      }
      setAnalyticsSlices((current) => ({
        ...current,
        [sliceName]: {
          status: 'error',
          error: err as Error,
        },
      }));
    }
  }, [historyDays]);

  const loadTodayComparison = useCallback(async (
    productMix: DashboardCockpitProductMix | null,
    requestId: number,
  ) => {
    if (!shouldLoadTodayComparison(productMix)) {
      setTodayComparisonStatus({ status: 'skipped', error: null });
      return;
    }

    setTodayComparisonStatus({ status: 'loading', error: null });
    try {
      const response = await fetchDashboardCockpitTodayComparison(historyDays);
      if (requestIdRef.current !== requestId) {
        return;
      }
      setCockpit((current) => (
        current ? mergeDashboardCockpitTodayComparison(current, response) : current
      ));
      setTodayComparisonStatus({ status: 'success', error: null });
    } catch (err) {
      if (requestIdRef.current !== requestId) {
        return;
      }
      setTodayComparisonStatus({ status: 'error', error: err as Error });
    }
  }, [historyDays]);

  const load = useCallback(async (mode: 'initial' | 'refresh' = 'initial') => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    if (mode === 'refresh') {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await fetchDashboardCockpit(historyDays);
      if (requestIdRef.current !== requestId) {
        return;
      }
      setCockpit(response);
      setPatterns(response.patterns);
      setAnalyticsSlices(buildAnalyticsState(response.productMix));
      setTodayComparisonStatus({ status: 'idle', error: null });
      setError(null);
      setLoading(false);
      setRefreshing(false);

      await loadTodayComparison(response.productMix, requestId);

      for (const sliceName of buildAnalyticsSliceQueue(response.productMix)) {
        await loadAnalyticsSlice(sliceName, requestId);
      }
    } catch (err) {
      if (requestIdRef.current !== requestId) {
        return;
      }
      setError(err as Error);
      setTodayComparisonStatus({ status: 'idle', error: null });
      setLoading(false);
      setRefreshing(false);
    }
  }, [historyDays, loadAnalyticsSlice, loadTodayComparison]);

  const retryAnalyticsSlice = useCallback(async (sliceName: DashboardCockpitAnalyticsSliceName) => {
    await loadAnalyticsSlice(sliceName, requestIdRef.current);
  }, [loadAnalyticsSlice]);

  useEffect(() => {
    load('initial');
  }, [load]);

  const isAnalyticsLoading = useMemo(
    () => Object.values(analyticsSlices).some((slice) => slice.status === 'loading'),
    [analyticsSlices],
  );

  return {
    cockpit,
    patterns,
    analyticsSlices,
    todayComparisonStatus,
    isLoading: loading,
    isRefreshing: refreshing,
    isAnalyticsLoading,
    error,
    refresh: () => load('refresh'),
    retryAnalyticsSlice,
  };
}

export default useDashboardCockpit;
